import React from 'react';

import {SpotAiAnalyticsClient} from '@modules/analytics/client';
import {getSegmentWriteKey} from '@modules/analytics/config';
import {getOrCreateDzrUniqId} from '@modules/analytics/uniqId';
import type {
	SpotAiClickAction,
	SpotAiScreenViewName,
} from '@modules/analytics/events';
import {useAuth} from '@providers/AuthProvider';

type TrackScreenViewInput = {
	screenViewName: SpotAiScreenViewName;
	score?: number | null;
	pageTitle?: string;
};

type TrackClickInput = TrackScreenViewInput & {
	action: SpotAiClickAction;
	userId?: string | null;
};

type LogEventContextValue = {
	isReady: boolean;
	userId: string | null;
	trackScreenView: (input: TrackScreenViewInput) => Promise<void>;
	trackSpotAiClick: (input: TrackClickInput) => Promise<void>;
};

const noop = async () => {};

const LogEventContext = React.createContext<LogEventContextValue>({
	isReady: false,
	userId: null,
	trackScreenView: noop,
	trackSpotAiClick: noop,
});

const logAnalyticsFailure = (eventName: string, error: unknown) => {
	if (process.env.NODE_ENV === 'test') return;
	// eslint-disable-next-line no-console -- telemetry failures should be visible without breaking UX.
	console.error(`[analytics] Failed to send ${eventName}`, error);
};

const warnMissingWriteKey = () => {
	if (process.env.NODE_ENV === 'test') return;
	// eslint-disable-next-line no-console -- missing production analytics config must be visible.
	console.warn(
		'[analytics] Missing Deezer Segment API key; analytics events are disabled.',
	);
};

export const LogEventProvider = ({children}: {children?: React.ReactNode}) => {
	const [{userId}] = useAuth();
	const [client, setClient] = React.useState<SpotAiAnalyticsClient | null>(
		null,
	);
	const [dzrUniqueId, setDzrUniqueId] = React.useState<string | null>(null);
	const warnedMissingKeyRef = React.useRef(false);

	React.useEffect(() => {
		const writeKey = getSegmentWriteKey();
		if (!writeKey) {
			if (!warnedMissingKeyRef.current) {
				warnMissingWriteKey();
				warnedMissingKeyRef.current = true;
			}
			return;
		}

		const uniqId = getOrCreateDzrUniqId();
		if (!uniqId) return;

		const analyticsClient = new SpotAiAnalyticsClient(writeKey);
		analyticsClient.setAnonymousId(uniqId);
		analyticsClient.init();
		void analyticsClient
			.identify({dzrUniqueId: uniqId, userId: null})
			.catch((error: unknown) => logAnalyticsFailure('identify', error));
		analyticsClient.openConsentGate();
		setDzrUniqueId(uniqId);
		setClient(analyticsClient);
	}, []);

	React.useEffect(() => {
		if (!client || !dzrUniqueId || !userId) return;

		void client
			.identify({dzrUniqueId, userId})
			.catch((error: unknown) => logAnalyticsFailure('identify', error));
	}, [client, dzrUniqueId, userId]);

	const trackScreenView = React.useCallback(
		async ({screenViewName, score, pageTitle}: TrackScreenViewInput) => {
			if (!client || !dzrUniqueId) return;
			await client.trackScreenView({
				dzrUniqueId,
				pageTitle,
				screenViewName,
				score,
				userId,
			});
		},
		[client, dzrUniqueId, userId],
	);

	const trackSpotAiClick = React.useCallback(
		async ({
			action,
			screenViewName,
			score,
			userId: inputUserId,
		}: TrackClickInput) => {
			if (!client || !dzrUniqueId) return;

			const eventUserId = inputUserId ?? userId;

			if (eventUserId) {
				await client.identify({dzrUniqueId, userId: eventUserId});
			}

			await client.trackClick({
				action,
				dzrUniqueId,
				screenViewName,
				score,
				userId: eventUserId ?? null,
			});
		},
		[client, dzrUniqueId, userId],
	);

	const value = React.useMemo<LogEventContextValue>(
		() => ({
			isReady: Boolean(client && dzrUniqueId),
			userId,
			trackScreenView: async (input) => {
				try {
					await trackScreenView(input);
				} catch (error) {
					logAnalyticsFailure('screen_view', error);
				}
			},
			trackSpotAiClick: async (input) => {
				try {
					await trackSpotAiClick(input);
				} catch (error) {
					logAnalyticsFailure('spot_ai_clicked', error);
				}
			},
		}),
		[client, dzrUniqueId, trackScreenView, trackSpotAiClick, userId],
	);

	return (
		<LogEventContext.Provider value={value}>
			{children}
		</LogEventContext.Provider>
	);
};

export const useLogEvent = (): LogEventContextValue =>
	React.useContext(LogEventContext);
